import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ImageUploader from '../components/ui/ImageUploader';
import PaymentSplitsSection from '../components/PaymentSplitsSection';
import RazorpayPaymentSplitsSection from '../components/RazorpayPaymentSplitsSection';
import { ArrowLeft, Save, CreditCard, Plus } from 'lucide-react';
import { toast } from 'sonner';

const EditCampaign = () => {
  const { campaignId } = useParams();
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    organization_name: '',
    description: '',
    logo_url: '',
    payment_gateway: 'razorpay',
    payment_account_id: '',
    payment_purpose: '',
    status: 'draft',
    max_registrations: '',
    fund_collection_mode: '',
  });

  useEffect(() => {
    if (!isSuperAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [campaignId, isSuperAdmin, navigate]);

  const fetchData = async () => {
    try {
      const [campaignRes, accountsRes] = await Promise.all([
        api.get(`/campaigns/${campaignId}`),
        api.get('/payment-accounts/list')
      ]);
      
      const campaign = campaignRes.data;
      setFormData({
        name: campaign.name || '',
        slug: campaign.slug || '',
        organization_name: campaign.organization_name || '',
        description: campaign.description || '',
        logo_url: campaign.logo_url || '',
        payment_gateway: campaign.payment_gateway || 'razorpay',
        payment_account_id: campaign.payment_account_id || '',
        payment_purpose: campaign.payment_purpose || '',
        status: campaign.status || 'draft',
        max_registrations: campaign.max_registrations || '',
        fund_collection_mode: campaign.fund_collection_mode || '',
      });
      
      setPaymentAccounts(accountsRes.data);
    } catch (error) {
      toast.error('Failed to load event data');
      navigate('/campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const selectedAccount = paymentAccounts.find(acc => acc.id === formData.payment_account_id);
      
      const payload = {
        name: formData.name,
        organization_name: formData.organization_name,
        description: formData.description || null,
        logo_url: formData.logo_url || null,
        payment_gateway: selectedAccount?.gateway_type || formData.payment_gateway,
        payment_account_id: formData.payment_account_id || null,
        payment_purpose: formData.payment_purpose || null,
        status: formData.status,
        max_registrations: formData.max_registrations ? parseInt(formData.max_registrations) : null,
        fund_collection_mode: formData.fund_collection_mode || null,
      };

      await api.put(`/campaigns/${campaignId}`, payload);
      toast.success('Event updated successfully!');
      navigate(`/campaigns/${campaignId}`);
    } catch (error) {
      // Activation gate — backend returns 400 with `redirect_to` when trying to
      // activate before all 4 email templates are filled in. Offer a one-click jump.
      const data = error.response?.data;
      if (error.response?.status === 400 && data?.redirect_to && data?.incomplete_triggers) {
        toast.error(
          (t) => (
            <div className="space-y-2">
              <p className="text-sm">{data.detail}</p>
              <p className="text-xs text-slate-500">
                Incomplete: {data.incomplete_triggers.join(', ')}
              </p>
              <button
                onClick={() => { toast.dismiss(t); navigate(data.redirect_to); }}
                className="text-xs font-semibold text-primary hover:underline"
              >
                Go to Email Templates →
              </button>
            </div>
          ),
          { duration: 8000 }
        );
      } else {
        toast.error(data?.detail || 'Failed to update event');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate(`/campaigns/${campaignId}`)}
              className="hover:bg-slate-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-outfit font-bold text-slate-900">Edit Event</h1>
              <p className="text-sm font-jakarta text-slate-600 mt-1">
                Update event details
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Campaign Name */}
            <div>
              <Label htmlFor="name" className="text-slate-700 font-jakarta font-medium mb-2 block">
                Event Name *
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., Annual Rotary Conference 2025"
                required
                className="h-11 rounded-lg border-slate-200 focus-visible:ring-primary"
              />
            </div>

            {/* Slug (read-only) */}
            <div>
              <Label htmlFor="slug" className="text-slate-700 font-jakarta font-medium mb-2 block">
                URL Slug
              </Label>
              <Input
                id="slug"
                value={formData.slug}
                disabled
                className="h-11 rounded-lg border-slate-200 bg-slate-50 text-slate-500"
              />
              <p className="text-xs text-slate-500 font-jakarta mt-1">
                Slug cannot be changed after creation
              </p>
            </div>

            {/* Organization Name */}
            <div>
              <Label htmlFor="organization_name" className="text-slate-700 font-jakarta font-medium mb-2 block">
                Organization Name *
              </Label>
              <Input
                id="organization_name"
                value={formData.organization_name}
                onChange={(e) => handleChange('organization_name', e.target.value)}
                placeholder="e.g., Rotary Club of Mumbai"
                required
                className="h-11 rounded-lg border-slate-200 focus-visible:ring-primary"
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description" className="text-slate-700 font-jakarta font-medium mb-2 block">
                Description
              </Label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Describe your event..."
                rows={3}
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Campaign Logo */}
            <div>
              <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
                Event Logo
              </Label>
              <ImageUploader
                value={formData.logo_url}
                onChange={(url) => handleChange('logo_url', url)}
                label="campaign-logo"
              />
              <p className="text-xs text-slate-500 font-jakarta mt-1">
                Upload an image for your event (recommended: 400x300px)
              </p>
            </div>

            {/* Status */}
            <div>
              <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
                Status *
              </Label>
              <Select
                value={formData.status}
                onValueChange={(value) => handleChange('status', value)}
              >
                <SelectTrigger className="h-11 rounded-lg border-slate-200 focus:ring-primary">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Fund Collection Mode */}
            <div>
              <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
                Fund Collection Mode *
              </Label>
              <Select
                value={formData.fund_collection_mode}
                onValueChange={(value) => handleChange('fund_collection_mode', value)}
              >
                <SelectTrigger className="h-11 rounded-lg border-slate-200 focus:ring-primary">
                  <SelectValue placeholder="Select fund collection mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="kaizen_managed">
                    <div className="flex flex-col">
                      <span className="font-medium">Kaizen Managed</span>
                      <span className="text-xs text-slate-500">Funds collected by Kaizen (2.95% platform fee)</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="organizer_direct">
                    <div className="flex flex-col">
                      <span className="font-medium">Direct to Organizer</span>
                      <span className="text-xs text-slate-500">Funds go directly to organizer's account</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 font-jakarta mt-1">
                {formData.fund_collection_mode === 'kaizen_managed' 
                  ? '✓ Kaizen collects funds and transfers to organizer after deducting 2.95% platform fee'
                  : formData.fund_collection_mode === 'organizer_direct'
                  ? '✓ Payments go directly to organizer\'s payment account'
                  : 'Choose how funds will be collected for this event'}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Payment Account */}
              <div>
                <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
                  Payment Account
                </Label>
                {paymentAccounts.length > 0 ? (
                  <Select
                    value={formData.payment_account_id}
                    onValueChange={(value) => {
                      const account = paymentAccounts.find(acc => acc.id === value);
                      setFormData(prev => ({ 
                        ...prev, 
                        payment_account_id: value,
                        payment_gateway: account?.gateway_type || prev.payment_gateway
                      }));
                    }}
                  >
                    <SelectTrigger className="h-11 rounded-lg border-slate-200 focus:ring-primary">
                      <SelectValue placeholder="Select payment account" />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentAccounts.map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          <div className="flex items-center space-x-2">
                            <CreditCard className="w-4 h-4 text-slate-400" />
                            <span>{account.name}</span>
                            <span className="text-xs text-slate-500 capitalize">({account.gateway_type})</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="p-3 border border-dashed border-slate-300 rounded-lg bg-slate-50">
                    <p className="text-sm text-slate-600 font-jakarta mb-2">No payment accounts configured</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/payment-accounts')}
                      className="text-primary border-primary hover:bg-primary/10"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Payment Account
                    </Button>
                  </div>
                )}
              </div>

              {/* Max Registrations */}
              <div>
                <Label htmlFor="max_registrations" className="text-slate-700 font-jakarta font-medium mb-2 block">
                  Max Registrations
                </Label>
                <Input
                  id="max_registrations"
                  type="number"
                  value={formData.max_registrations}
                  onChange={(e) => handleChange('max_registrations', e.target.value)}
                  placeholder="Leave empty for unlimited"
                  className="h-11 rounded-lg border-slate-200 focus-visible:ring-primary"
                />
              </div>
            </div>

            {/* Payment Purpose */}
            {formData.payment_account_id && (
              <div>
                <Label htmlFor="payment_purpose" className="text-slate-700 font-jakarta font-medium mb-2 block">
                  Payment Purpose
                </Label>
                <Input
                  id="payment_purpose"
                  value={formData.payment_purpose}
                  onChange={(e) => handleChange('payment_purpose', e.target.value)}
                  placeholder="e.g., Discon_2026, Annual_Conference_2025"
                  className="h-11 rounded-lg border-slate-200 focus-visible:ring-primary"
                />
                <p className="text-xs text-slate-500 font-jakarta mt-1">
                  This will be displayed on the payment gateway page
                </p>
              </div>
            )}

            {/* Payment Splits — Cashfree multi-vendor configuration (visible only when applicable) */}
            <PaymentSplitsSection
              campaignId={campaignId}
              paymentGateway={formData.payment_gateway}
              fundCollectionMode={formData.fund_collection_mode}
            />

            {/* Payment Splits — Razorpay Route multi-vendor configuration (visible only when applicable) */}
            <RazorpayPaymentSplitsSection
              campaignId={campaignId}
              paymentGateway={formData.payment_gateway}
              fundCollectionMode={formData.fund_collection_mode}
            />

            {/* Actions */}
            <div className="flex items-center justify-end space-x-4 pt-6 border-t border-slate-100">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/campaigns/${campaignId}`)}
                className="border-slate-200 hover:bg-slate-50"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-primary hover:bg-primary-hover text-primary-foreground font-jakarta font-medium rounded-lg"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default EditCampaign;
